import {getRangeWaveform} from './rangeWaveform.js';
import {waveformSpeechRange} from './waveformBoundaries.js';
import {speechHandleRange} from './speechHandles.js';

// A couple of ~0.8s ranged reads at the cut edges, even for a four-hour file.
// Missing audio, CORS, network errors and ambiguous peaks all fall back to the
// conservative transcript handles. Nothing is ever changed during playback.
const PAST=.56,FUTURE=.56;
function edgeWindows(range,duration){
 const start=[Math.max(0,range.start-PAST),Math.min(duration,range.start+.26)];
 const end=[Math.max(0,range.end-.26),Math.min(duration,range.end+FUTURE)];
 return {start,end};
}
function untouched(block,source,paper){
 if(!block?.originalRange||!source?.words?.length||!block.automaticHandles)return false;
 const locked=paper?.refine?.lockedHandles?.[block.id];
 if(locked&&(Math.abs(locked.start-block.start)>.00001||Math.abs(locked.end-block.end)>.00001))return false;
 // Distinguish hand-adjusted boundaries from original automatic handles.
 const baseline=speechHandleRange(source.words,block.originalRange,source.duration);
 if(!locked&&!block.boundaryAnalysis&&
   (Math.abs(baseline.start-block.start)>.00001||Math.abs(baseline.end-block.end)>.00001))return false;
 return true;
}
export async function assistWaveformPassages(paper,blockIds,resolveUrl,{readRange=getRangeWaveform}={}){
 const selected=new Set(blockIds||[]);
 if(!selected.size||!paper?.blocks?.length||typeof resolveUrl!=='function')return paper;
 const urls=new Map();
 const urlFor=id=>{
  if(!urls.has(id))urls.set(id,Promise.resolve().then(()=>resolveUrl(id)));
  return urls.get(id);
 };
 const replacements=await Promise.all(paper.blocks.map(async block=>{
  if(!selected.has(block.id))return block;
  const source=paper.sources?.[block.sourceId];
  if(!untouched(block,source,paper))return block;
  try{
   const url=await urlFor(block.sourceId);
   if(typeof url!=='string'||!url)return block;
   const range=block.originalRange,windows=edgeWindows(range,source.duration);
   const [start,end]=await Promise.allSettled([
    windows.start[1]>windows.start[0]?readRange(url,...windows.start):Promise.resolve(null),
    windows.end[1]>windows.end[0]?readRange(url,...windows.end):Promise.resolve(null)
   ]);
   const waves={start:start.status==='fulfilled'?start.value:null,end:end.status==='fulfilled'?end.value:null};
   if(!waves.start&&!waves.end)return block;
   const adjusted=waveformSpeechRange(source.words,range,waves,source.duration);
   if(Math.abs(adjusted.start-block.start)<.008&&Math.abs(adjusted.end-block.end)<.008)return block;
   return {...block,start:adjusted.start,end:adjusted.end,
    boundaryAnalysis:adjusted.report,
    boundaryBaseline:block.boundaryBaseline||{start:block.start,end:block.end}};
  }catch{return block;}
 }));
 return replacements.some((block,i)=>block!==paper.blocks[i])?{...paper,blocks:replacements}:paper;
}
